import { create } from 'zustand';
import {
  timeEntriesAPI,
  TimeEntry,
  TimeEntryCreate,
  TimeEntryUpdate,
  DailySummary,
  DateRangeSummary
} from '../services/api';

interface TimeEntryState {
  entries: TimeEntry[];
  dailySummary: DailySummary | null;
  rangeSummary: DateRangeSummary | null;
  selectedDate: string; // ISO date string (YYYY-MM-DD)
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchEntries: (params?: {
    date?: string;
    start_date?: string;
    end_date?: string;
    project_id?: string;
    client_id?: string;
  }) => Promise<void>;
  fetchDailySummary: (date: string) => Promise<DailySummary | null>;
  fetchRangeSummary: (startDate: string, endDate: string) => Promise<DateRangeSummary | null>;
  createEntry: (data: TimeEntryCreate) => Promise<TimeEntry>;
  updateEntry: (id: string, data: TimeEntryUpdate) => Promise<TimeEntry>;
  deleteEntry: (id: string) => Promise<void>;
  setSelectedDate: (date: string) => void;
  clearError: () => void;
}

// Get today's date in YYYY-MM-DD format (local timezone)
const getTodayString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to extract error message from API response (handles Pydantic validation errors)
const getErrorMessage = (error: any, defaultMsg: string): string => {
  const detail = error.response?.data?.detail;
  if (Array.isArray(detail)) {
    // Pydantic validation errors: [{loc: [...], msg: "...", type: "..."}]
    return detail.map((e: any) => `${e.loc?.slice(1).join('.')}: ${e.msg}`).join(', ') || defaultMsg;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  return defaultMsg;
};

export const useTimeEntryStore = create<TimeEntryState>((set, get) => ({
  entries: [],
  dailySummary: null,
  rangeSummary: null,
  selectedDate: getTodayString(),
  isLoading: false,
  error: null,

  fetchEntries: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const entries = await timeEntriesAPI.list(params);
      set({ entries, isLoading: false });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to fetch time entries'),
        isLoading: false
      });
    }
  },

  fetchDailySummary: async (date: string) => {
    set({ isLoading: true, error: null });
    try {
      const summary = await timeEntriesAPI.getDailySummary(date);
      set({ dailySummary: summary, entries: summary.entries, isLoading: false });
      return summary;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to fetch daily summary'),
        isLoading: false
      });
      return null;
    }
  },

  fetchRangeSummary: async (startDate: string, endDate: string) => {
    set({ isLoading: true, error: null });
    try {
      const summary = await timeEntriesAPI.getSummary(startDate, endDate);
      set({ rangeSummary: summary, isLoading: false });
      return summary;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to fetch summary'),
        isLoading: false
      });
      return null;
    }
  },

  createEntry: async (data: TimeEntryCreate) => {
    set({ isLoading: true, error: null });
    try {
      const entry = await timeEntriesAPI.create(data);
      // Refresh daily summary if the entry is for the selected date
      const { selectedDate, fetchDailySummary } = get();
      if (data.date === selectedDate) {
        await fetchDailySummary(selectedDate);
      }
      set({ isLoading: false });
      return entry;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to create time entry'),
        isLoading: false
      });
      throw error;
    }
  },

  updateEntry: async (id: string, data: TimeEntryUpdate) => {
    set({ isLoading: true, error: null });
    try {
      const entry = await timeEntriesAPI.update(id, data);
      // Refresh daily summary
      const { selectedDate, fetchDailySummary } = get();
      await fetchDailySummary(selectedDate);
      set({ isLoading: false });
      return entry;
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to update time entry'),
        isLoading: false
      });
      throw error;
    }
  },

  deleteEntry: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await timeEntriesAPI.delete(id);
      // Refresh daily summary
      const { selectedDate, fetchDailySummary } = get();
      await fetchDailySummary(selectedDate);
      set({ isLoading: false });
    } catch (error: any) {
      set({
        error: getErrorMessage(error, 'Failed to delete time entry'),
        isLoading: false
      });
      throw error;
    }
  },

  setSelectedDate: (date: string) => {
    set({ selectedDate: date });
    // Automatically fetch daily summary when date changes
    get().fetchDailySummary(date);
  },

  clearError: () => set({ error: null }),
}));
